/** @format */

import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import { ShareModal } from 'src/components/ShareModal';
import ExtranetContentBody from './ExtranetContentBody';
import ExtranetContentActionButtons from './ExtranetContentActionButtons';
import useExtranetContentDetailState from './useExtranetContentDetailState';
import useExtranetContentActions from './useExtranetContentActions';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import { useCallback, useMemo, useState } from 'react';
import { GA4_COMPONENT_NAME, GA4_COMPONENT_POSITION, GA4_COMPONENT_TYPE } from 'src/analytics/componentInteractionGa4';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import { getExtranetLanguageMenuItems, resolveExtranetSelectableLocale } from 'src/utils/extranetLanguages';
import { getDefaultSearchLanguage } from 'src/slices/search';
import { SHOW_CONTENT_LANGUAGE_SWITCH } from 'src/constants';
import useDeviceDetect from 'src/hooks/useDeviceDetect';
import NotFoundView from '../errors/NotFoundView';

export default function ExtranetContentDetail() {
  const navigate = useNavigate();
  const { contentID, locale } = useParams();
  const { t } = useTranslation();
  const { trackComponentInteraction } = useGa4Analytics();
  const { contentDetail, shouldShowSkeleton, shouldDimDetail, title, hasFatalError } = useExtranetContentDetailState(
    contentID,
    locale
  );
  const { isShareModalOpen, shareUrl, handlePrint, handleShareClick, handleShareModalClose } =
    useExtranetContentActions();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const { isMobile } = useDeviceDetect();

  const selectedLanguage = useMemo(() => resolveExtranetSelectableLocale(locale || 'en'), [locale]);
  const languageMenuItems = useMemo(() => getExtranetLanguageMenuItems(t), [t]);
  const selectedLanguageLabel = useMemo(() => {
    const selectedItem = languageMenuItems.find(item => item.value === selectedLanguage);
    return selectedItem?.label || t('common.selectLanguage');
  }, [languageMenuItems, selectedLanguage, t]);

  const location = useLocation();
  const isFromCreateContent = location.state?.fromCreateContent;

  const handleClickLanguage = language => {
    navigate(`/extranet/content/${contentID}/${language}`);
  };

  const handleBackClick = useCallback(() => {
    trackComponentInteraction({
      component_name: GA4_COMPONENT_NAME.BACK_BUTTON,
      component_type: GA4_COMPONENT_TYPE.BUTTON,
      component_position: GA4_COMPONENT_POSITION.EXTRANET_CONTENT_DETAIL
    });
    history.go(-1);
  }, [navigate, trackComponentInteraction]);

  const handleBackToHomeClick = useCallback(() => {
    trackComponentInteraction({
      component_name: GA4_COMPONENT_NAME.BACK_TO_HOME,
      component_type: GA4_COMPONENT_TYPE.BUTTON,
      component_position: GA4_COMPONENT_POSITION.EXTRANET_CONTENT_DETAIL
    });
    navigate('/', { state: { resetExtranetTopicNav: true } });
  }, [navigate, trackComponentInteraction]);

  if (hasFatalError) return <NotFoundView />;

  return (
    <Page className="topics-content-detail" title={title}>
      <Grid className="container container--narrow topics-content-detail--language-selector-container">
        <Breadcrumb
          data={[
            { title: 'Extranet', link: { pathname: '/extranet', state: { resetExtranetTopicNav: true } } },
            { title }
          ]}
          additionalClasses="container--no-padding"
        />
        {SHOW_CONTENT_LANGUAGE_SWITCH ? (
          <div className="topics-content-detail--language-selector">
            <CustomDropDown
              placement={isMobile ? 'bottomLeft' : 'bottomRight'}
              value={selectedLanguage}
              defaultValue={getDefaultSearchLanguage()}
              onReset={handleClickLanguage}
              resetAriaLabel={t('common.button.resetToDefault')}
              open={isLanguageDropdownOpen}
              onOpenChange={open => {
                setIsLanguageDropdownOpen(open);
              }}
              trigger={
                <button
                  className="topics-page__controls-btn topics-page__control-language-btn"
                  type="button"
                  aria-expanded={isLanguageDropdownOpen}>
                  <span>{selectedLanguageLabel}</span>
                </button>
              }>
              <ul className="dropdown-selection">
                {languageMenuItems.map(item => (
                  <li key={item.key}>
                    <button
                      className={`topics-page__control-menu-item${
                        item.value === selectedLanguage ? ' topics-page__control-menu-item--active' : ''
                      }`}
                      type="button"
                      onClick={() => handleClickLanguage(item.value)}>
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </CustomDropDown>
          </div>
        ) : null}
      </Grid>
      <Grid className="container container--narrow">
        <ExtranetContentBody
          detail={contentDetail}
          showSkeleton={shouldShowSkeleton}
          isLoadingOverlay={shouldDimDetail}
        />
        <div className="topics-content-detail__action-buttons">
          <div className="topics-content-detail__action-buttons-left">
            {isFromCreateContent ? (
              <Button
                onClick={() => navigate('/administration/contents')}
                label={t('administrationPage.backToContentDashboard')}
              />
            ) : (
              <>
                <Button secondary onClick={handleBackClick} label={t('common.button.backbtn')} />
                <Button onClick={handleBackToHomeClick} label={t('common.button.backToHome')} />
              </>
            )}
          </div>
          <div className="topics-content-detail__action-buttons-right">
            <ExtranetContentActionButtons
              onPrint={handlePrint}
              onShare={handleShareClick}
              className="topics-content-detail__icon-btn"
            />
          </div>
        </div>
      </Grid>
      <ShareModal isOpen={isShareModalOpen} onClose={handleShareModalClose} shareUrl={shareUrl} />
    </Page>
  );
}
